'use client';

import { useEffect, useState } from 'react';

interface ImportProgressModalProps {
  isOpen: boolean;
  isMinimized?: boolean;
  onClose: () => void;
  onCancel?: () => void;
  onMinimize?: () => void;
  totalFiles: number;
  currentFile: string;
  importedCount: number;
  isComplete: boolean;
  isCancelling?: boolean;
  error?: string;
}

export default function ImportProgressModal({
  isOpen,
  isMinimized,
  onClose,
  onCancel,
  onMinimize,
  totalFiles,
  currentFile,
  importedCount,
  isComplete,
  isCancelling,
  error
}: ImportProgressModalProps) {
  const [dots, setDots] = useState('');

  useEffect(() => {
    if (!isComplete && !error) {
      const interval = setInterval(() => {
        setDots(prev => (prev.length >= 3 ? '' : prev + '.'));
      }, 500);
      return () => clearInterval(interval);
    }
  }, [isComplete, error]);

  // Don't render modal if minimized or not open
  if (!isOpen || isMinimized) return null;

  const progress = totalFiles > 0 ? (importedCount / totalFiles) * 100 : 0;

  return (
    <div
      className="fixed inset-0 flex items-center justify-center z-50"
      style={{ background: 'rgba(0, 0, 0, 0.7)' }}
    >
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
        {/* Header */}
        <div className="p-6 border-b">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold text-gray-900">Importing from Google Drive</h2>
            {isComplete && (
              <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
                ✕
              </button>
            )}
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          {error ? (
            <div className="text-center">
              <div className="text-red-500 text-4xl mb-4">❌</div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">Import Failed</h3>
              <p className="text-red-600 text-sm mb-4">{error}</p>
              <button
                onClick={onClose}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
              >
                Close
              </button>
            </div>
          ) : isComplete ? (
            <div className="text-center">
              <div className="text-green-500 text-4xl mb-4">✅</div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">Import Complete!</h3>
              <p className="text-gray-600 text-sm mb-4">
                Successfully imported {importedCount} file{importedCount !== 1 ? 's' : ''} from
                Google Drive
              </p>
              <button
                onClick={onClose}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
              >
                Done
              </button>
            </div>
          ) : (
            <div>
              {/* Progress Bar */}
              <div className="mb-4">
                <div className="flex justify-between text-sm text-gray-600 mb-2">
                  <span>Progress</span>
                  <span>
                    {importedCount} / {totalFiles}
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className={`h-2 rounded-full transition-all duration-300 ${isCancelling ? 'bg-orange-600' : 'bg-blue-600'}`}
                    style={{ width: `${progress}%` }}
                  ></div>
                </div>
              </div>

              {/* Current File */}
              <div className="text-center">
                <div
                  className={`text-3xl mb-3 ${isCancelling ? 'text-orange-500' : 'text-blue-500'}`}
                >
                  {isCancelling ? '⏸️' : '📥'}
                </div>
                <p className="text-sm text-gray-600 mb-2">
                  {isCancelling
                    ? 'Cancelling and rolling back...'
                    : currentFile
                      ? 'Downloading'
                      : 'Preparing import'}
                  {!isCancelling && dots}
                </p>
                {currentFile && !isCancelling && (
                  <p className="text-sm font-medium text-gray-900 truncate">{currentFile}</p>
                )}
              </div>

              {/* Action Buttons */}
              {!isCancelling && (
                <div className="mt-6 flex justify-center space-x-3">
                  {onMinimize && (
                    <button
                      onClick={onMinimize}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                    >
                      Work in Background
                    </button>
                  )}
                  {onCancel && (
                    <button
                      onClick={onCancel}
                      className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                    >
                      Cancel Import
                    </button>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
